import "./styles.css";
import { useEffect, useRef, useState } from "react";
import useLangStore from "@/hooks/useLangStore";
import useFetch from "@/hooks/useFetch";
import usePopupStore from "@/hooks/usePopupStore";
import translations from "@/config/translations";
import Table, { type TableColumn } from "@/UI/components/Table";
import TableCell from "@/UI/components/Table/TableCell";
import SidePopup from "@/UI/popups/SidePopup";
import ProductsForm from "@/UI/forms/ProductsForm";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";

const PAGE_SIZE = 12;

interface ProductCategory {
    _id: string;
    name: string;
    picture: string | null;
    sort: number;
}

interface ProductSize {
    title: string;
    showTitle: boolean;
    price: number;
    discount: { type: "percentage" | "fixed"; amount: number } | null;
}

interface Product {
    _id: string;
    name: string;
    description: string;
    pictures: string[];
    temperature: "hot" | "cold" | null;
    sizes: ProductSize[];
    discount: { type: "percentage" | "fixed"; amount: number } | null;
    category: ProductCategory[];
    sort: number;
}

export default function ProductsListScreen() {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();
    const [page, setPage] = useState(1);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    // ── Products fetch ────────────────────────────────────────────────────────
    const { data, loading, invoke } = useFetch("products", { query: { page: 1, limit: PAGE_SIZE } });

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        invoke({ query: { page, limit: PAGE_SIZE } });
    }, [page]);

    useEffect(() => {
        const handler = () => {
            setPage(1);
            invoke({ query: { page: 1, limit: PAGE_SIZE } });
        };
        window.addEventListener("products:refresh", handler);
        return () => window.removeEventListener("products:refresh", handler);
    }, []);

    const products: Product[] = data?.data?.products ?? [];
    const total: number = data?.data?.total ?? 0;

    // ── Delete fetch ──────────────────────────────────────────────────────────
    const { loading: deleting, status: deleteStatus, invoke: deleteInvoke } = useFetch("products", { method: "DELETE" }, false);

    const wasDeleting = useRef(false);
    useEffect(() => {
        if (deleting) {
            wasDeleting.current = true;
        } else if (wasDeleting.current) {
            wasDeleting.current = false;
            setDeletingId(null);
            if (deleteStatus === 200) invoke({ query: { page, limit: PAGE_SIZE } });
        }
    }, [deleting]);

    const handleDelete = (id: string) => {
        if (deleting) return;
        setDeletingId(id);
        deleteInvoke({ param: id });
    };

    const openDeleteConfirm = (product: Product) => {
        const name = "confirm-delete";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message: t.products.deleteConfirm,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                onConfirm: () => handleDelete(product._id),
            },
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Get-one fetch (edit / duplicate) ──────────────────────────────────────
    const pendingModeRef = useRef<"edit" | "duplicate" | null>(null);
    const { loading: fetchingOne, data: oneData, invoke: fetchOne } = useFetch("products", { method: "GET" }, false);

    const openProductForm = (mode: "edit" | "duplicate", product: any) => {
        const name = mode === "edit" ? "edit-product" : "duplicate-product";
        const title = mode === "edit" ? t.productForm.editTitle : t.productForm.duplicateTitle;
        removeComp(name);
        addComp({
            name,
            Component: SidePopup,
            details: {
                title,
                Content: ProductsForm,
                contentDetails: {
                    productId: mode === "edit" ? product._id : undefined,
                    initialData: product,
                    isDuplicate: mode === "duplicate",
                    onSuccess: () => {
                        invoke({ query: { page: 1, limit: PAGE_SIZE } });
                        setPage(1);
                    },
                },
            },
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
        });
    };

    const wasFetchingOne = useRef(false);
    useEffect(() => {
        if (fetchingOne) {
            wasFetchingOne.current = true;
        } else if (wasFetchingOne.current) {
            wasFetchingOne.current = false;
            const product = oneData?.data?.product;
            if (product && pendingModeRef.current) {
                openProductForm(pendingModeRef.current, product);
                pendingModeRef.current = null;
            }
        }
    }, [fetchingOne]);

    const handleEdit = (row: Product) => {
        pendingModeRef.current = "edit";
        fetchOne({ param: row._id });
    };

    const handleDuplicate = (row: Product) => {
        pendingModeRef.current = "duplicate";
        fetchOne({ param: row._id });
    };

    // ── Columns ───────────────────────────────────────────────────────────────
    const columns: TableColumn<Product>[] = [
        {
            key: "pictures",
            header: t.products.col.image,
            width: "76px",
            render: (row) => <TableCell.Gallery images={row.pictures} alt={row.name} />,
        },
        {
            key: "name",
            header: t.products.col.name,
            width: "170px",
            render: (row) => <TableCell.Text value={row.name} maxChars={20} />,
        },
        {
            key: "description",
            header: t.products.col.description,
            width: "200px",
            render: (row) => <TableCell.Description value={row.description || "—"} title={t.products.col.description} maxChars={25} />,
        },
        {
            key: "category",
            header: t.products.col.category,
            width: "180px",
            render: (row) => (
                <div className="products-categories-wrap">
                    {row.category && row.category.length > 0 ? (
                        row.category.slice(0, 2).map((cat) => <TableCell.Badge key={cat._id} label={cat.name} variant="orange" />)
                    ) : (
                        <TableCell.Badge label="—" variant="gray" />
                    )}
                    {row.category && row.category.length > 2 && <span className="products-categories-more">+{row.category.length - 2}</span>}
                </div>
            ),
        },
        {
            key: "temperature",
            header: t.products.col.temperature,
            width: "70px",
            render: (row) =>
                row.temperature ? (
                    <div className="temp-icon-wrap">
                        <img
                            src={row.temperature === "hot" ? "/icons/hot-icon.svg" : "/icons/cold-icon.svg"}
                            alt={t.products.temp[row.temperature]}
                            width={18}
                            height={18}
                        />
                    </div>
                ) : null,
        },
        {
            key: "sizes",
            header: t.products.col.sizes,
            width: "90px",
            render: (row) => <TableCell.Number value={row.sizes?.length ?? 0} />,
        },
        {
            key: "sort",
            header: t.products.col.sort,
            width: "90px",
            render: (row) => <TableCell.Number value={row.sort} />,
        },
        {
            key: "actions",
            header: t.products.col.actions,
            width: "60px",
            render: (row) => (
                <TableCell.Actions
                    disabled={deletingId === row._id || fetchingOne}
                    actions={[
                        { name: "edit", icon: "/icons/edit-icon.svg", handler: () => handleEdit(row) },
                        { name: "duplicate", icon: "/icons/duplicate-icon.svg", handler: () => handleDuplicate(row) },
                        { name: "delete", icon: "/icons/delete-icon.svg", handler: () => openDeleteConfirm(row), danger: true },
                    ]}
                />
            ),
        },
    ];

    return (
        <div className="products-list-screen">
            <Table
                columns={columns}
                data={products}
                loading={loading}
                rowKey="_id"
                pagination={{ page, pageSize: PAGE_SIZE, total, onPageChange: setPage }}
            />
        </div>
    );
}
